package com.kaihuoy.home

import androidx.compose.runtime.*
import com.kaihuoy.components.common.*
import com.kaihuoy.shared.data.Announcement
import com.kaihuoy.shared.data.PerehdytysVastaus
import com.kaihuoy.shared.data.ScheduledMaintenance
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import com.kaihuoy.web.initializeKaihuPushNotifications
import com.kaihuoy.web.router.AppRouterState
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

// ======================
// Päädashboard
// ======================

@Composable
fun MainDashboardScreen(user: User?, isAdmin: Boolean, onLogout: () -> Unit) {
    val firstName = user?.displayName?.split(" ")?.firstOrNull() ?: "Käyttäjä"
    val greeting = remember(firstName) { buildTimeBasedGreeting(firstName) }
    val isHuolto = user?.isHuolto == true
    var announcements by remember { mutableStateOf<List<Announcement>>(emptyList()) }
    var isLoadingAnnouncements by remember { mutableStateOf(true) }
    var pendingServiceCount by remember { mutableStateOf(0) }
    var overdueServiceCount by remember { mutableStateOf(0) }
    var machinesInUse by remember { mutableStateOf(0) }
    var machinesFree by remember { mutableStateOf(0) }
    var machinesOnSite by remember { mutableStateOf(0) }
    var vastaukset by remember { mutableStateOf<List<PerehdytysVastaus>>(emptyList()) }
    var maintenanceList by remember { mutableStateOf<List<ScheduledMaintenance>>(emptyList()) }

    LaunchedEffect(user?.uid, isAdmin, isHuolto) {
        if (!isAdmin && !isHuolto) return@LaunchedEffect
        try {
            FirebaseService.database.reference("scheduled_maintenance").valueEvents.collect { snapshot ->
                var pending = 0; var overdue = 0
                val items = mutableListOf<ScheduledMaintenance>()
                snapshot.children.forEach { child ->
                    try {
                        val item = child.value<ScheduledMaintenance>()
                        val withId = if (item.id.isBlank()) item.copy(id = child.key ?: "") else item
                        items += withId
                        val s = withId.status
                        if (s == "reported" || s == "scheduled") pending++
                        if (s == "overdue") overdue++
                    } catch (_: Exception) {}
                }
                pendingServiceCount = pending
                overdueServiceCount = overdue
                maintenanceList = items
            }
        } catch (e: Exception) { console.error("Huoltomäärän haku epäonnistui", e) }
    }

    LaunchedEffect(user?.uid, isAdmin, isHuolto) {
        if (!isAdmin && !isHuolto) return@LaunchedEffect
        try {
            FirebaseService.database.reference("machines").valueEvents.collect { snapshot ->
                var inUse = 0; var free = 0; var onSite = 0
                snapshot.children.forEach { child ->
                    try {
                        val s = child.child("status").value<String>()
                        when (s) { "käytössä" -> inUse++; "työmaalla" -> onSite++; "ei käytössä" -> free++; else -> free++ }
                    } catch (_: Exception) { free++ }
                }
                machinesInUse = inUse; machinesFree = free; machinesOnSite = onSite
            }
        } catch (e: Exception) { console.error("Koneiden haku epäonnistui", e) }
    }

    LaunchedEffect(user?.uid) {
        val currentUser = user ?: run {
            announcements = emptyList()
            isLoadingAnnouncements = false
            return@LaunchedEffect
        }

        isLoadingAnnouncements = true

        launch {
            try {
                FirebaseService.database.reference("perehdytys_vastaukset").valueEvents.collect { snapshot ->
                    vastaukset = snapshot.children.mapNotNull { child ->
                        try { val v = child.value<PerehdytysVastaus>(); if (v.id.isBlank()) v.copy(id = child.key ?: "") else v }
                        catch (_: Exception) { null }
                    }.sortedByDescending { it.aika }
                }
            } catch (_: Exception) {}
        }

        try {
            FirebaseService.database.reference("announcements").valueEvents.collect { snapshot ->
                val now = Date.now().toLong()
                announcements = snapshot.children.mapNotNull { child ->
                    try {
                        val announcement = child.value<Announcement>()
                        if (announcement.id.isBlank()) announcement.copy(id = child.key ?: "") else announcement
                    } catch (e: Exception) {
                        console.error("Virhe ilmoituksen luvussa: ${child.key}", e)
                        null
                    }
                }
                    .filter { isAnnouncementVisibleOnHome(it, now) }
                    .sortedWith(compareByDescending<Announcement> { it.isPinned }.thenByDescending { announcementSortTime(it) })

                isLoadingAnnouncements = false
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            // Normaali peruutus kun navigoidaan pois — ei tehdä mitään
        } catch (e: Exception) {
            console.error("Ilmoitusten haku epäonnistui käyttäjälle ${currentUser.uid}", e)
            announcements = emptyList()
            isLoadingAnnouncements = false
        }
    }

    if (window.innerWidth > 900) {
        DesktopDashboard(user,greeting,isAdmin,isHuolto,pendingServiceCount, overdueServiceCount, machinesInUse, machinesFree, machinesOnSite, announcements, isLoadingAnnouncements, vastaukset, maintenanceList, onLogout)
    } else {
        MobileDashboard(user,greeting, isAdmin, isHuolto, pendingServiceCount, overdueServiceCount, machinesInUse, machinesFree, machinesOnSite, onLogout, announcements, isLoadingAnnouncements, vastaukset, maintenanceList)
    }
}

@Composable
private fun DesktopDashboard(
    user: User?,
    greeting: String,
    isAdmin: Boolean,
    isHuolto: Boolean,
    pendingServiceCount: Int,
    overdueServiceCount: Int,
    machinesInUse: Int,
    machinesFree: Int,
    machinesOnSite: Int,
    announcements: List<Announcement>,
    isLoadingAnnouncements: Boolean,
    vastaukset: List<PerehdytysVastaus>,
    maintenanceList: List<ScheduledMaintenance>,
    onLogout: () -> Unit

)
{
    PageLayout(greeting = getGreeting(user?.displayName), onLogout = onLogout) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            minHeight(100.vh); backgroundColor(KaihuTheme.Background)
            width(100.percent)
        }
    }) {
        // ── Yläpalkki ──────────────────────────────────────────


        // ── Sisältö ─────────────────────────────────────────────

        Div(attrs = { style { padding(20.px, 32.px, 0.px, 32.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(24.px) } }) {
            // Yläosa: HeroBanner + StatusPanel rinnakkain
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr 1fr")
                    gap(20.px)
                    property("align-items", "stretch")
                }
            }) {
                HeroBanner(announcements, isLoadingAnnouncements, isAdmin)
                StatusPanel(machinesInUse, machinesFree, machinesOnSite, pendingServiceCount, overdueServiceCount, isAdmin || isHuolto)
            }

            // Alaosa: roolikohtaiset kortit
            RoleBasedSection(user,greeting, isAdmin, isHuolto, vastaukset, maintenanceList)
        }
    }
    }
}

// ── HeroBanner ──────────────────────────────────────────────────────
@Composable
private fun HeroBanner(announcements: List<Announcement>, isLoading: Boolean, isAdmin: Boolean) {
    val startIndex = announcements.indexOfFirst { it.isPinned }.takeIf { it >= 0 } ?: 0
    var currentIndex by remember(announcements.size) { mutableStateOf(startIndex) }
    val announcement = announcements.getOrNull(currentIndex)
    val isShortMessage = (announcement?.message?.length ?: 0) <= 220
    var expanded by remember(announcement?.id) { mutableStateOf(false) }
    var hovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        style {
            borderRadius(16.px); padding(28.px)
            property("border", "1px solid ${if (hovered) "#22d3ee" else KaihuTheme.Border}")
            property("box-shadow", if (hovered) "0 4px 24px rgba(34,211,238,0.12)" else KaihuTheme.ShadowMd)
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            property("background", if (hovered) "linear-gradient(135deg, #22d3ee18 0%, #131625 100%)" else KaihuTheme.CardGradient)
            property("transition", "all 0.18s")
            height(100.percent)
            boxSizing("border-box")
        }
    }) {
        when {
            isLoading -> P(attrs = { style { color(KaihuTheme.TextSecondary); margin(0.px) } }) { Text("Ladataan...") }
            announcement == null -> {
                Div {
                    Span(attrs = {
                        style {
                            backgroundColor(rgba(34, 211, 238, 0.12)); color(KaihuTheme.PrimaryTeal)
                            padding(4.px, 12.px); borderRadius(999.px); fontSize(0.78.em); fontWeight("700")
                        }
                    }) { Text("Ilmoitus") }
                    H2(attrs = { style { margin(16.px, 0.px, 8.px, 0.px); color(KaihuTheme.TextPrimary); fontSize(1.8.em); fontWeight("800"); lineHeight("1.2") } }) {
                        Text("Ei ilmoituksia")
                    }
                    P(attrs = { style { color(KaihuTheme.TextSecondary); margin(0.px); lineHeight("1.5") } }) {
                        Text(if (isAdmin) "Luo ensimmäinen ilmoitus Hallinta-sivulla." else "Admin ei ole julkaissut ilmoituksia.")
                    }
                }
            }
            else -> {
                Div(attrs = { style { flex(1) } }) {
                    // Badget
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap); marginBottom(16.px) } }) {
                        if (announcement.isPinned) {
                            Span(attrs = {
                                style {
                                    backgroundColor(rgba(34, 211, 238, 0.12)); color(KaihuTheme.PrimaryTeal)
                                    padding(4.px, 12.px); borderRadius(999.px); fontSize(0.78.em); fontWeight("700")
                                }
                            }) { TIcon("pin"); Text(" Kiinnitetty") }
                        }
                        Span(attrs = {
                            style {
                                backgroundColor(rgba(255,255,255,0.06)); color(KaihuTheme.TextSecondary)
                                padding(4.px, 12.px); borderRadius(999.px); fontSize(0.78.em); fontWeight("600")
                            }
                        }) { Text("${currentIndex + 1} / ${announcements.size}") }
                    }

                    H2(attrs = {
                        style {
                            margin(0.px, 0.px, 12.px, 0.px); color(KaihuTheme.TextPrimary)
                            fontSize(1.7.em); fontWeight("800"); lineHeight("1.2")
                        }
                    }) { Text(announcement.title) }

                    // Viesti: aina koko teksti jos lyhyt, muuten katkaistu kunnes avataan
                    if (isShortMessage || expanded) {
                        P(attrs = {
                            style {
                                color(KaihuTheme.TextSecondary); margin(0.px, 0.px, 16.px, 0.px)
                                lineHeight("1.7"); property("white-space", "pre-wrap")
                            }
                        }) { Text(announcement.message) }
                    } else {
                        P(attrs = {
                            style {
                                color(KaihuTheme.TextSecondary); margin(0.px); lineHeight("1.55")
                                property("display", "-webkit-box"); property("-webkit-line-clamp", "3")
                                property("-webkit-box-orient", "vertical"); property("overflow", "hidden")
                            }
                        }) { Text(announcement.message) }
                    }
                }

                // Alurivi: meta + napit
                Div(attrs = { style { property("margin-top", "auto"); paddingTop(16.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) } }) {
                    // Napit ensin, sitten metatieto
                    SmallMetaText(buildAnnouncementMeta(announcement))
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); flexWrap(FlexWrap.Wrap) } }) {
                        // ← → ensin
                        NavButton(text = "←", isEnabled = currentIndex > 0, onClick = { currentIndex--; expanded = false }
                        )
                        NavButton(text = "→", isEnabled = currentIndex < announcements.lastIndex, onClick = { currentIndex++; expanded = false }
                        )
                        // Avaa / Sulje
                        if (!isShortMessage) {
                            Span(attrs = {
                                onClick { expanded = !expanded }
                                style {
                                    backgroundColor(KaihuTheme.PrimaryTeal); color(KaihuTheme.Background)
                                    padding(8.px, 20.px); borderRadius(8.px); fontSize(0.88.em); fontWeight("700")
                                    cursor("pointer"); property("transition", "opacity 0.15s")
                                }
                            }) { Text(if (expanded) "Sulje ↑" else "Avaa →") }
                        }
                        // Poista kiinnitys viimeisenä
                        if (isAdmin && announcement.isPinned) {
                            unPinButton(announcement, accentHex = "#DC2626")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun unPinButton(announcement: Announcement, accentHex: String)  {

    var isUnpinning by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    var hovered by remember { mutableStateOf(false) }

    Span(attrs = {
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        onClick {
            if (!isUnpinning) {
                isUnpinning = true
                scope.launch {
                    try {
                        FirebaseService.database.reference("announcements").child(announcement.id).child("isPinned").setValue(false)
                    } catch (_: Exception) {}
                    isUnpinning = false
                }
            }
        }
        style {
            display(DisplayStyle.InlineBlock)
            backgroundColor(KaihuTheme.CardBackground); color(KaihuTheme.TextSecondary)
            padding(6.px, 14.px); borderRadius(4.px); fontSize(0.82.em); fontWeight("600")
            cursor(if (isUnpinning) "default" else "pointer")
            borderRadius(12.px); padding(10.px, 14.px); cursor("pointer")
            property("border", "1px solid ${if (hovered) accentHex else "#2a2f45"}")
            property("transition", "all 0.18s")
            property("background", if (hovered) "linear-gradient(135deg, ${accentHex}22 0%, ${accentHex}0a 100%)" else "linear-gradient(135deg, #1e2235 0%, #131625 100%)")
            property("box-shadow", if (hovered) "0 4px 16px ${accentHex}33" else "none")
        }
    }) {
        if (!isUnpinning) TIcon("pin"); Text(if (isUnpinning) " Poistetaan..." else " Poista kiinnitys")
    }
}

// ── StatusPanel ────────────────────────────────────────────────────
@Composable
private fun StatusPanel(
    machinesInUse: Int, machinesFree: Int, machinesOnSite: Int,
    pendingServiceCount: Int, overdueServiceCount: Int,
    showStats: Boolean
) {
    var activeTab by remember { mutableStateOf("kalusto") }
    var hovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        style {
            borderRadius(16.px); padding(24.px)
            property("border", "1px solid ${if (hovered) "#22d3ee" else KaihuTheme.Border}")
            property("box-shadow", if (hovered) "0 4px 24px rgba(34,211,238,0.12)" else KaihuTheme.ShadowMd)
            property("background", if (hovered) "linear-gradient(135deg, #22d3ee18 0%, #131625 100%)" else KaihuTheme.CardGradient)
            property("transition", "all 0.18s")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(20.px)
        }
    }) {
        // Välilehdet
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); gap(4.px)
                backgroundColor(KaihuTheme.CardBackground); borderRadius(10.px); padding(4.px)
                property("width", "fit-content")
            }
        }) {
            listOf("kalusto" to "Kalusto", "huolto" to "Huollot").forEach { (key, label) ->
                val isActive = activeTab == key
                Span(attrs = {
                    onClick { activeTab = key }
                    style {
                        padding(6.px, 16.px); borderRadius(8.px); fontSize(0.85.em); fontWeight("700")
                        cursor("pointer")
                        backgroundColor(if (isActive) KaihuTheme.PrimaryTeal else Color.transparent)
                        color(if (isActive) KaihuTheme.Background else KaihuTheme.TextSecondary)
                        property("transition", "all 0.15s")
                    }
                }) { Text(label) }
            }
        }

        if (!showStats) {
            P(attrs = { style { color(KaihuTheme.TextSecondary); margin(0.px); fontSize(0.9.em) } }) {
                Text("Tilastot näkyvät ylläpitäjille ja huollolle.")
            }
        } else if (activeTab == "kalusto") {
            Div(attrs = { style { display(DisplayStyle.Grid); property("grid-template-columns", "repeat(3, 1fr)"); gap(12.px) } }) {
                DarkStatCard("Käytössä", machinesInUse.toString(), "#22d3ee", "koneet")
                DarkStatCard("Ei käytössä", machinesFree.toString(), "#DC2626", "koneet")
                DarkStatCard("Työmaalla", machinesOnSite.toString(), "#F59E0B", "koneet")
            }
        } else {
            Div(attrs = { style { display(DisplayStyle.Grid); property("grid-template-columns", "repeat(2, 1fr)"); gap(12.px) } }) {
                DarkStatCard("Avoimet", pendingServiceCount.toString(), if (pendingServiceCount > 0) "#F59E0B" else "#10B981", "huoltoilmoitukset")
                DarkStatCard("Myöhässä", overdueServiceCount.toString(), if (overdueServiceCount > 0) "#EF4444" else "#10B981", "huoltoilmoitukset")
            }
        }
    }
}

@Composable
fun NavButton(
    text: String,               // Nappiin tuleva teksti (esim. "←" tai "→")
    isEnabled: Boolean,         // Onko nappi aktiivinen (esim. currentIndex > 0)
    onClick: () -> Unit         // Mitä tapahtuu kun nappia painetaan
) {
    // TÄRKEÄÄ: Tämä tila on funktion sisällä, joten hover toimii vain tälle napille!
    var hovered by remember { mutableStateOf(false) }

    Span(attrs = {
        // Tapahtumankuuntelijat
        onMouseOver { if (isEnabled) hovered = true }
        onMouseOut { hovered = false }
        onClick { if (isEnabled) onClick() }

        style {
            // Perustyylit
            backgroundColor(KaihuTheme.CardBackground)
            color(if (isEnabled) KaihuTheme.TextPrimary else KaihuTheme.TextLight)
            padding(8.px, 14.px)
            borderRadius(8.px)
            fontSize(0.88.em)
            fontWeight("700")
            cursor(if (isEnabled) "pointer" else "default")

            // Dynaamiset tyylit (hover-efektit)
            property("border", "1px solid ${if (hovered && isEnabled) "#22d3ee" else KaihuTheme.Border}")
            property("transition", "all 0.2s ease-in-out")

            if (hovered && isEnabled) {
                property("box-shadow", "0 4px 24px rgba(34,211,238,0.12)")
                property("background", "linear-gradient(135deg, #22d3ee18 0%, #131625 100%)")
            } else {
                property("box-shadow", KaihuTheme.ShadowMd)
                property("background", KaihuTheme.CardGradient)
            }
        }
    }) {
        Text(text)
    }
}
@Composable
private fun DarkStatCard(label: String, value: String, accentHex: String, route: String) {
    var hovered by remember { mutableStateOf(false) }
    Div(attrs = {
        onClick { AppRouterState.navigate(route) }
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        style {
            borderRadius(12.px); padding(16.px, 14.px); cursor("pointer")
            property("border", "1px solid ${if (hovered) accentHex else "#2a2f45"}")
            property("transition", "all 0.18s")
            property("background", if (hovered) "linear-gradient(135deg, ${accentHex}22 0%, ${accentHex}0a 100%)" else "linear-gradient(135deg, #1e2235 0%, #131625 100%)")
            property("box-shadow", if (hovered) "0 4px 16px ${accentHex}33" else "none")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px)
        }
    }) {
        Span(attrs = { style { fontSize(1.9.em); fontWeight("800"); color(Color(accentHex)); lineHeight("1") } }) { Text(value) }
        Span(attrs = { style { fontSize(0.8.em); color(KaihuTheme.TextSecondary); fontWeight("600") } }) { Text(label) }
    }
}

// ── RoleBasedSection ───────────────────────────────────────────────
@Composable
private fun RoleBasedSection(
    user: User?,
    greeting: String,
    isAdmin: Boolean,
    isHuolto: Boolean,
    vastaukset: List<PerehdytysVastaus>,
    maintenanceList: List<ScheduledMaintenance>
) {
    when {
        isAdmin -> Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "1fr 1fr")
                gap(20.px)
            }
        }) {
            ViimeinenToiminta(vastaukset, maintenanceList)
            TulevatHuollot(maintenanceList)
        }
        isHuolto -> TulevatHuollot(maintenanceList)
        else -> {}
    }
}

// ── ViimeinenToiminta ──────────────────────────────────────────────
private data class ActivityItem(val dot: String, val person: String, val action: String, val time: Long)

@Composable
private fun ViimeinenToiminta(vastaukset: List<PerehdytysVastaus>, maintenanceList: List<ScheduledMaintenance>) {
    val activities = remember(vastaukset, maintenanceList) {
        val list = mutableListOf<ActivityItem>()
        vastaukset.take(15).forEach { v ->
            list += ActivityItem("#22d3ee", v.userName.ifBlank { "Tuntematon" }, "perehdytys: ${v.pohjaOtsikko.ifBlank { "—" }}", v.aika)
        }
        maintenanceList.sortedByDescending { it.timestamp }.take(15).forEach { m ->
            val dot = when (m.status) { "completed" -> "#10B981"; "overdue" -> "#EF4444"; else -> "#F59E0B" }
            list += ActivityItem(dot, m.assignedTo.ifBlank { "Huolto" }, "${m.serviceType.ifBlank { "Huolto" }}: ${m.machineNumber.ifBlank { m.title }}", m.timestamp)
        }
        list.sortedByDescending { it.time }
    }
    var hoveredV by remember { mutableStateOf(false) }

    Div(attrs = {
        onMouseOver { hoveredV = true }
        onMouseOut { hoveredV = false }
        style {
            borderRadius(16.px)
            property("border", "1px solid ${if (hoveredV) "#22d3ee" else KaihuTheme.Border}")
            property("box-shadow", if (hoveredV) "0 4px 24px rgba(34,211,238,0.12)" else KaihuTheme.ShadowMd)
            property("background", if (hoveredV) "linear-gradient(135deg, #22d3ee18 0%, #131625 100%)" else KaihuTheme.CardGradient)
            property("transition", "all 0.18s")
            property("overflow", "hidden")
        }
    }) {
        Div(attrs = {
            style {
                padding(18.px, 24.px)
                property("border-bottom", "1px solid ${KaihuTheme.Border}")
            }
        }) {
            H3(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontWeight("700") } }) {
                Text("Viimeisin toiminta")
            }
        }

        if (activities.isEmpty()) {
            Div(attrs = { style { padding(32.px, 24.px); color(KaihuTheme.TextSecondary); fontSize(0.9.em); textAlign("center") } }) {
                Text("Ei toimintaa vielä.")
            }
        } else {
            Div(attrs = { style { overflowY("auto"); maxHeight(260.px) } }) {
                activities.forEach { item ->
                    val dateStr = if (item.time > 0) Date(item.time.toDouble()).toLocaleDateString("fi-FI") else "—"
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.FlexStart); gap(12.px)
                            padding(12.px, 24.px)
                            property("border-bottom", "1px solid ${KaihuTheme.Divider}")
                        }
                    }) {
                        Div(attrs = {
                            style {
                                width(8.px); height(8.px); borderRadius(50.percent)
                                backgroundColor(Color(item.dot))
                                property("flex-shrink", "0")
                                property("margin-top", "6px")
                            }
                        })
                        Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(2.px); flex(1) } }) {
                            Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.88.em); fontWeight("600") } }) { Text(item.person) }
                            Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.82.em) } }) { Text(item.action) }
                        }
                        Span(attrs = { style { color(KaihuTheme.TextLight); fontSize(0.78.em); property("white-space", "nowrap") } }) { Text(dateStr) }
                    }
                }
            }
        }
    }
}

// ── TulevatHuollot ─────────────────────────────────────────────────
@Composable
private fun TulevatHuollot(maintenanceList: List<ScheduledMaintenance>) {
    val upcoming = remember(maintenanceList) {
        maintenanceList
            .filter { it.status != "completed" }
            .sortedWith(compareBy({ if (it.status == "overdue") 0 else 1 }, { it.scheduledDate }))
    }
    var hoveredH by remember { mutableStateOf(false) }

    Div(attrs = {
        onMouseOver { hoveredH = true }
        onMouseOut { hoveredH = false }
        style {
            borderRadius(16.px)
            property("border", "1px solid ${if (hoveredH) "#22d3ee" else KaihuTheme.Border}")
            property("box-shadow", if (hoveredH) "0 4px 24px rgba(34,211,238,0.12)" else KaihuTheme.ShadowMd)
            property("background", if (hoveredH) "linear-gradient(135deg, #22d3ee18 0%, #131625 100%)" else KaihuTheme.CardGradient)
            property("transition", "all 0.18s")
            property("overflow", "hidden")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                padding(18.px, 24.px)
                property("border-bottom", "1px solid ${KaihuTheme.Border}")
            }
        }) {
            H3(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontWeight("700") } }) {
                Text("Tulevat huollot")
            }
            Span(attrs = {
                onClick { AppRouterState.navigate("huoltoilmoitukset") }
                style {
                    color(KaihuTheme.PrimaryTeal); fontSize(0.82.em); fontWeight("700"); cursor("pointer")
                }
            }) { Text("Kaikki →") }
        }

        if (upcoming.isEmpty()) {
            Div(attrs = { style { padding(32.px, 24.px); color(KaihuTheme.TextSecondary); fontSize(0.9.em); textAlign("center") } }) {
                Text("Ei avoimia huoltoja.")
            }
        } else {
            Div(attrs = { style { overflowY("auto"); maxHeight(260.px) } }) {
            upcoming.forEach { m ->
                val urgencyColor = when (m.status) { "overdue" -> "#EF4444"; "reported" -> "#F59E0B"; else -> "#22d3ee" }
                val dateStr = if (m.scheduledDate > 0) Date(m.scheduledDate.toDouble()).toLocaleDateString("fi-FI") else "—"
                var rowHovered by remember { mutableStateOf(false) }
                Div(attrs = {
                    onClick { AppRouterState.navigate("huoltoilmoitukset", highlightId = m.id) }
                    onMouseOver { rowHovered = true }
                    onMouseOut { rowHovered = false }
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px)
                        padding(12.px, 24.px)
                        property("border-bottom", "1px solid ${KaihuTheme.Divider}")
                        cursor("pointer")
                        property("background", if (rowHovered) "rgba(34,211,238,0.06)" else "transparent")
                        property("transition", "background 0.13s")
                    }
                }) {
                    Div(attrs = {
                        style {
                            width(4.px); height(36.px); borderRadius(4.px)
                            backgroundColor(Color(urgencyColor))
                            property("flex-shrink", "0")
                        }
                    })
                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(2.px); flex(1) } }) {
                        Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.88.em); fontWeight("600") } }) {
                            Text("${m.machineNumber.ifBlank { "Kone" }} — ${m.title.ifBlank { m.serviceType }}")
                        }
                        Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.8.em) } }) {
                            Text(m.serviceType.ifBlank { "Huolto" })
                        }
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.FlexEnd); gap(2.px) } }) {
                        Span(attrs = { style { color(Color(urgencyColor)); fontSize(0.78.em); fontWeight("700") } }) {
                            Text(when (m.status) { "overdue" -> "Myöhässä"; "completed" -> "Valmis"; "reported" -> "Ilmoitettu"; else -> "Ajastettu" })
                        }
                        Span(attrs = { style { color(KaihuTheme.TextLight); fontSize(0.78.em) } }) { Text(dateStr) }
                    }
                }
            }
        }
    }
}

// ── MaterialTable ──────────────────────────────────────────────────
@Composable
fun MaterialTable(vastaukset: List<PerehdytysVastaus>, isAdmin: Boolean) {
    var activeTab by remember { mutableStateOf("avoimet") }
    val cutoff = Date.now().toLong() - 30L * 24 * 60 * 60 * 1000
    val avoimet = vastaukset.filter { it.aika >= cutoff }
    val arkisto  = vastaukset.filter { it.aika < cutoff }
    val rows = if (activeTab == "avoimet") avoimet else arkisto

    Div(attrs = {
        style {
            borderRadius(16.px)
            property("border", "1px solid ${KaihuTheme.Border}")
            property("box-shadow", KaihuTheme.ShadowMd)
            property("background", KaihuTheme.CardGradient)
            property("overflow", "hidden")
        }
    }) {
        // Otsikkorivi + tabsit
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center); padding(18.px, 24.px)
                property("border-bottom", "1px solid ${KaihuTheme.Border}")
            }
        }) {
            H3(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontWeight("700") } }) {
                Text("Perehdytykset")
            }
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(4.px)
                    backgroundColor(KaihuTheme.CardBackground); borderRadius(10.px); padding(4.px)
                }
            }) {
                listOf("avoimet" to "Viimeisimmät (30 pv)", "arkisto" to "Arkisto").forEach { (key, label) ->
                    val isActive = activeTab == key
                    Span(attrs = {
                        onClick { activeTab = key }
                        style {
                            padding(6.px, 14.px); borderRadius(8.px); fontSize(0.82.em); fontWeight("700")
                            cursor("pointer")
                            backgroundColor(if (isActive) KaihuTheme.PrimaryTeal else Color.transparent)
                            color(if (isActive) KaihuTheme.Background else KaihuTheme.TextSecondary)
                            property("transition", "all 0.15s")
                        }
                    }) { Text(label) }
                }
            }
        }

        // Taulukon otsikot
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "2fr 1.5fr 1.2fr 1fr auto")
                padding(10.px, 24.px)
                property("border-bottom", "1px solid ${KaihuTheme.Border}")
            }
        }) {
            listOf("Henkilö", "Pohja", "Työmaa", "Päivämäärä", "").forEach { col ->
                Span(attrs = { style { fontSize(0.75.em); fontWeight("700"); color(KaihuTheme.TextLight); property("text-transform", "uppercase"); property("letter-spacing", "0.06em") } }) { Text(col) }
            }
        }

        // Rivit
        if (rows.isEmpty()) {
            Div(attrs = { style { padding(32.px, 24.px); textAlign("center"); color(KaihuTheme.TextSecondary); fontSize(0.9.em) } }) {
                Text(if (activeTab == "avoimet") "Ei perehdytyksiä viimeisen 30 päivän aikana." else "Arkisto on tyhjä.")
            }
        } else {
            rows.take(8).forEachIndexed { i, v ->
                val dateStr = if (v.aika > 0) Date(v.aika.toDouble()).toLocaleDateString("fi-FI") else "-"
                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "2fr 1.5fr 1.2fr 1fr auto")
                        padding(13.px, 24.px)
                        alignItems(AlignItems.Center)
                        backgroundColor(if (i % 2 == 0) Color.transparent else KaihuTheme.CardBackground)
                        property("border-bottom", "1px solid ${KaihuTheme.Border}")
                    }
                }) {
                    Span(attrs = { style { color(KaihuTheme.TextPrimary); fontWeight("500"); fontSize(0.9.em) } }) { Text(v.userName.ifBlank { "—" }) }
                    Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em) } }) { Text(v.pohjaOtsikko.ifBlank { "—" }) }
                    Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em) } }) { Text(v.tyomaa.ifBlank { "—" }) }
                    Span(attrs = { style { color(KaihuTheme.TextLight); fontSize(0.85.em) } }) { Text(dateStr) }
                    if (isAdmin) {
                        Span(attrs = {
                            onClick { AppRouterState.navigate("hallinta") }
                            style {
                                color(KaihuTheme.PrimaryTeal); cursor("pointer"); fontSize(0.82.em)
                                fontWeight("700"); property("white-space", "nowrap")
                            }
                        }) { Text("Avaa →") }
                    } else {
                        Span {}
                    }
                }
            }
            }
        }
    }
}

// ======================
// Mobiili dashboard (DashboardCardit)
// ======================

@Composable
private fun MobileDashboard(
    user: User?,
    greeting: String,
    isAdmin: Boolean,
    isHuolto: Boolean,
    pendingServiceCount: Int,
    overdueServiceCount: Int,
    machinesInUse: Int,
    machinesFree: Int,
    machinesOnSite: Int,
    onLogout: () -> Unit,
    announcements: List<Announcement>,
    isLoadingAnnouncements: Boolean,
    vastaukset: List<PerehdytysVastaus> = emptyList(),
    maintenanceList: List<ScheduledMaintenance> = emptyList()
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            minHeight(100.vh)
            width(100.percent)
            backgroundColor(KaihuTheme.Background)
        }
    }) {

        PageLayout(greeting = getGreeting(user?.displayName), onLogout = onLogout) {
            Div(attrs = {
                style {
                    padding(12.px, 0.px)
                    maxWidth(900.px)
                    property("margin", "0 auto")
                    boxSizing("border-box")
                    width(100.percent)
                    flexGrow(1)
                }
            }) {

                Div(attrs = {
                    style {
                        marginBottom(32.px)
                    }
                })


                PushNotificationCard(user = user, compact = true)

                if (isAdmin || isHuolto) {
                    Div(attrs = { style { marginBottom(20.px) } }) {
                        StatusPanel(
                            machinesInUse,
                            machinesFree,
                            machinesOnSite,
                            pendingServiceCount,
                            overdueServiceCount,
                            true
                        )
                    }
                }

                HeroBanner(announcements, isLoadingAnnouncements, isAdmin)


                if (isAdmin || isHuolto) {
                    Div(attrs = {
                        style {
                            marginTop(20.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(
                            16.px
                        )
                        }
                    }) {
                        if (isAdmin) ViimeinenToiminta(vastaukset, maintenanceList)
                        TulevatHuollot(maintenanceList)
                    }
                }
            }
        }
    }
}

@Composable
private fun PushNotificationCard(user: User?, compact: Boolean) {
    val currentUser = user ?: return
    val scope = rememberCoroutineScope()
    var isEnabling by remember(currentUser.uid) { mutableStateOf(false) }
    var permissionState by remember(currentUser.uid) { mutableStateOf(currentNotificationPermissionState()) }
    var notificationsEnabled by remember(currentUser.uid) { mutableStateOf(false) }

    val buttonText = when {
        isEnabling && permissionState == NotificationPermissionState.DEFAULT -> "Sallitaan ilmoitukset..."
        isEnabling -> "Otetaan käyttöön..."
        notificationsEnabled -> "Ilmoitukset käytössä"
        permissionState == NotificationPermissionState.DENIED -> "Ilmoitukset estetty"
        permissionState == NotificationPermissionState.UNSUPPORTED -> "Ei tuettu tässä selaimessa"
        permissionState == NotificationPermissionState.DEFAULT -> "Salli ilmoitukset"
        else -> "Ota ilmoitukset käyttöön"
    }

    val helperText = when {
        notificationsEnabled -> "Ilmoitukset ovat käytössä tällä laitteella."
        permissionState == NotificationPermissionState.DENIED -> "Salli ilmoitukset Chromen sivustoasetuksista ja kokeile sitten uudelleen."
        permissionState == NotificationPermissionState.UNSUPPORTED -> "Tämä selain ei tue selainilmoituksia."
        permissionState == NotificationPermissionState.DEFAULT -> "Selain kysyy seuraavaksi luvan ilmoituksille."
        else -> "Ilmoituslupa on jo sallittu. Ota ilmoitukset käyttöön tällä laitteella napista."
    }

    val buttonEnabled = !isEnabling && !notificationsEnabled &&
        permissionState != NotificationPermissionState.DENIED &&
        permissionState != NotificationPermissionState.UNSUPPORTED

    Div(attrs = {
        classes(AppStyles.cardShadow)
        style {
            property("background", KaihuTheme.CardGradient)
            borderRadius(20.px)
            padding(if (compact) 18.px else 24.px)
            property("box-shadow", AppStyles.cardShadow)
            property("border", "1px solid ${KaihuTheme.Divider}")
            marginBottom(if (compact) 18.px else 24.px)
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                gap(16.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Div(attrs = { style { flex(1) } }) {
                H3(attrs = { style { margin(0.px, 0.px, 6.px, 0.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("Selainilmoitukset")
                }
                P(attrs = { style { margin(0.px); color(KaihuTheme.TextSecondary) } }) {
                    Text(helperText)
                }
            }

            StyledButton(
                text = buttonText,
                enabled = buttonEnabled,
                backgroundColor = if (notificationsEnabled) KaihuTheme.Success else KaihuTheme.PrimaryTeal,
                onClick = {
                    scope.launch {
                        isEnabling = true
                        try {
                            val result = initializeKaihuPushNotifications(
                                userId = currentUser.uid,
                                displayName = currentUser.displayName.ifBlank { "Käyttäjä" }
                            )
                            permissionState = currentNotificationPermissionState()
                            notificationsEnabled = result.ok
                            if (result.ok) {
                                com.kaihuoy.components.common.AppFeedback.showSuccess(result.message)
                            } else {
                                com.kaihuoy.components.common.AppFeedback.showError(result.message)
                            }
                        } catch (e: Throwable) {
                            permissionState = currentNotificationPermissionState()
                            console.error("Ilmoitusten käyttöönotto epäonnistui", e)
                            com.kaihuoy.components.common.AppFeedback.showError("Ilmoitusten käyttöönotto epäonnistui.")
                        } finally {
                            isEnabling = false
                        }
                    }
                }
            )
        }
    }
}

private enum class NotificationPermissionState {
    DEFAULT,
    GRANTED,
    DENIED,
    UNSUPPORTED
}

private fun currentNotificationPermissionState(): NotificationPermissionState {
    val permission = window.asDynamic().Notification?.permission as? String
        ?: return NotificationPermissionState.UNSUPPORTED

    return when (permission) {
        "granted" -> NotificationPermissionState.GRANTED
        "denied" -> NotificationPermissionState.DENIED
        else -> NotificationPermissionState.DEFAULT
    }
}

@Composable
private fun AnnouncementHeroSection(
    announcement: Announcement?,
    isLoading: Boolean,
    isAdmin: Boolean,
    compact: Boolean
) {
    Div(attrs = {
        classes(AppStyles.cardShadow)
        style {
            property("background", KaihuTheme.CardGradient)
            borderRadius(24.px)
            padding(if (compact) 22.px else 32.px)
            property("box-shadow", AppStyles.cardShadow)
            property("border", "1px solid ${KaihuTheme.Divider}")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                gap(12.px)
                marginBottom(16.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Div {
                H2(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontSize(if (compact) 1.5.em else 2.em) } }) {
                    Text("Ajankohtaista")
                }
                P(attrs = { style { margin(6.px, 0.px, 0.px, 0.px); color(KaihuTheme.TextSecondary) } }) {
                    Text("Kiinnitetty ilmoitus")
                }
            }

            if (isAdmin) {
                StyledButton(
                    text = "Avaa ilmoitukset",
                    onClick = { AppRouterState.navigate("hallinta") },
                    backgroundColor = KaihuTheme.PrimaryTeal
                )
            }
        }

        when {
            isLoading -> P(attrs = { style { color(KaihuTheme.TextSecondary); margin(0.px) } }) {
                Text("Ladataan ilmoituksia...")
            }

            announcement == null -> EmptyAnnouncementsState(isAdmin)

            else -> {
                AnnouncementBadgeRow(announcement)

                H3(attrs = {
                    style {
                        margin(10.px, 0.px, 8.px, 0.px)
                        color(KaihuTheme.TextPrimary)
                        fontSize(if (compact) 1.35.em else 1.7.em)
                    }
                }) {
                    Text(announcement.title)
                }

                P(attrs = {
                    style {
                        color(KaihuTheme.TextSecondary)
                        margin(0.px, 0.px, 16.px, 0.px)
                        property("white-space", "pre-wrap")
                        lineHeight("1.6")
                    }
                }) {
                    Text(announcement.message)
                }

                SmallMetaText(buildAnnouncementMeta(announcement))
            }
        }
    }
}

@Composable
private fun AnnouncementListCard(announcement: Announcement) {
    Div(attrs = {
        style {
            padding(18.px, 0.px)
            property("border-top", "1px solid ${KaihuTheme.Divider}")
        }
    }) {
        AnnouncementBadgeRow(announcement)

        H4(attrs = { style { margin(10.px, 0.px, 8.px, 0.px); color(KaihuTheme.TextPrimary) } }) {
            Text(announcement.title)
        }

        P(attrs = {
            style {
                margin(0.px, 0.px, 8.px, 0.px)
                color(KaihuTheme.TextSecondary)
                property("white-space", "pre-wrap")
                lineHeight("1.55")
            }
        }) {
            Text(announcement.message)
        }

        SmallMetaText(buildAnnouncementMeta(announcement))
    }
}

@Composable
private fun AnnouncementBadgeRow(announcement: Announcement) {
    val now = Date.now().toLong()
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            gap(8.px)
            flexWrap(FlexWrap.Wrap)
            alignItems(AlignItems.Center)
        }
    }) {
        if (announcement.isPinned) {
            InlineBadge("Kiinnitetty")
        }
        InlineBadge("Ilmoitus")
        buildExpiryBadgeText(announcement.expiresAt, now)?.let { badgeText ->
            val badgeColors = buildExpiryBadgeColors(announcement.expiresAt, now)
            InlineBadge(
                text = badgeText,
                background = badgeColors.first,
                foreground = badgeColors.second
            )
        }
    }
}

@Composable
private fun InlineBadge(
    text: String,
    background: CSSColorValue = Color("#ECFEFF"),
    foreground: CSSColorValue = KaihuTheme.PrimaryTeal
) {
    Span(attrs = {
        style {
            backgroundColor(background)
            color(foreground)
            padding(6.px, 10.px)
            borderRadius(999.px)
            fontSize(0.82.em)
            fontWeight("700")
        }
    }) {
        Text(text)
    }
}

@Composable
private fun EmptyAnnouncementsState(isAdmin: Boolean) {
    P(attrs = { style { color(KaihuTheme.TextSecondary); margin(0.px) } }) {
        Text(
            if (isAdmin) {
                "Ilmoituksia ei vielä ole. Luo ensimmäinen Hallinta-sivun Ilmoitukset-välilehdellä."
            } else {
                "Admin ei ole vielä julkaissut ilmoituksia."
            }
        )
    }
}

@Composable
private fun SmallMetaText(text: String) {
    P(attrs = { style { margin(0.px); color(KaihuTheme.TextLight); fontSize(0.9.em) } }) {
        Text(text)
    }
}

private fun formatAnnouncementTimestamp(timestamp: Long): String {
    if (timestamp <= 0) return "-"
    return Date(timestamp.toDouble()).toLocaleString("fi-FI")
}

private fun buildAnnouncementMeta(announcement: Announcement): String {
    val parts = mutableListOf<String>()
    parts += "Julkaistu ${formatAnnouncementTimestamp(announcement.timestamp)}"
    if (announcement.updatedAt > announcement.timestamp) {
        parts += "Päivitetty ${formatAnnouncementTimestamp(announcement.updatedAt)}"
    }
    if (announcement.expiresAt > 0) {
        parts += "Voimassa ${formatAnnouncementTimestamp(announcement.expiresAt)} asti"
    }
    parts += announcement.createdByName.ifBlank { "KaihuOY" }
    return parts.joinToString(" · ")
}

private fun isAnnouncementVisibleOnHome(announcement: Announcement, now: Long): Boolean {
    return announcement.isActive && !announcement.isArchived && (announcement.expiresAt == 0L || announcement.expiresAt > now)
}

private fun announcementSortTime(announcement: Announcement): Long = maxOf(announcement.timestamp, announcement.updatedAt)

private fun buildExpiryBadgeText(expiresAt: Long, now: Long): String? {
    if (expiresAt <= 0) return null
    return if (expiresAt <= now) {
        "Vanhentunut"
    } else {
        "Voimassa asti ${formatAnnouncementDate(expiresAt)}"
    }
}

private fun buildExpiryBadgeColors(expiresAt: Long, now: Long): Pair<CSSColorValue, CSSColorValue> {
    return when {
        expiresAt <= 0 -> Color("#ECFEFF") to KaihuTheme.PrimaryTeal
        expiresAt <= now -> Color("#F3F4F6") to KaihuTheme.TextSecondary
        expiresAt - now <= 3L * 24 * 60 * 60 * 1000 -> Color("#FFF7ED") to Color("#C2410C")
        else -> Color("#EEF2FF") to Color("#4338CA")
    }
}

private fun formatAnnouncementDate(timestamp: Long): String {
    if (timestamp <= 0) return "-"
    return Date(timestamp.toDouble()).toLocaleDateString("fi-FI")
}

private fun buildTimeBasedGreeting(firstName: String): String {
    val hour = Date().getHours()
    val prefix = when (hour) {
        in 5..9 -> "Hyvää huomenta"
        in 10..16 -> "Hyvää päivää"
        in 17..22 -> "Hyvää iltaa"
        else -> "Hei"
    }
    return "$prefix, $firstName"
}

// ======================
// Admin/Huolto statcards
// ======================

@Composable
private fun AdminStatSection(
    pendingServiceCount: Int,
    overdueServiceCount: Int,
    machinesInUse: Int,
    machinesFree: Int,
    machinesOnSite: Int,
    compact: Boolean
) {
    val mb = if (compact) 18.px else 24.px

    Div(attrs = { style { marginBottom(mb) } }) {
        // Kone-statit
        H3(attrs = { style { margin(0.px, 0.px, 12.px, 0.px); color(KaihuTheme.TextPrimary); fontSize(1.em); fontWeight("700") } }) {
            Text("Kalusto")
        }
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(3, 1fr)")
                gap(12.px)
                marginBottom(mb)
            }
        }) {
            QuickStatCard("circle-filled", "Käytössä", machinesInUse.toString(), Color("#DCFCE7"), Color("#15803D"), "koneet")
            QuickStatCard("circle-x", "Ei käytössä", machinesFree.toString(), Color("#FFF1F2"), Color("#BE123C"), "koneet")
            QuickStatCard("map-pin", "Työmaalla", machinesOnSite.toString(), Color("#FEF9C3"), Color("#A16207"), "koneet")
        }

        // Huolto-statit
        H3(attrs = { style { margin(0.px, 0.px, 12.px, 0.px); color(KaihuTheme.TextPrimary); fontSize(1.em); fontWeight("700") } }) {
            Text("Huollot")
        }
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(3, 1fr)")
                gap(12.px)
            }
        }) {
            QuickStatCard("clipboard-list", "Avoimet", pendingServiceCount.toString(),
                if (pendingServiceCount > 0) Color("#FFF7ED") else Color("#F0FDF4"),
                if (pendingServiceCount > 0) Color("#EA580C") else Color("#15803D"),
                "huoltoilmoitukset")
            QuickStatCard("alert-triangle", "Myöhässä", overdueServiceCount.toString(),
                if (overdueServiceCount > 0) Color("#FFF1F2") else Color("#F0FDF4"),
                if (overdueServiceCount > 0) Color("#BE123C") else Color("#15803D"),
                "huoltoilmoitukset")
            QuickStatCard("plus", "Kirjaa huolto", "", Color("#EFF6FF"), Color("#1D4ED8"), "uusi_huolto")
        }
    }
}

@Composable
private fun QuickStatCard(icon: String, label: String, value: String, bg: CSSColorValue, valueColor: CSSColorValue, route: String) {
    Div(attrs = {
        onClick { AppRouterState.navigate(route) }
        style {
            backgroundColor(bg)
            borderRadius(16.px)
            padding(16.px)
            cursor("pointer")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.FlexStart)
            gap(6.px)
            property("transition", "filter 0.15s")
        }
    }) {
        TIcon(icon, 1.3.em)
        if (value.isNotBlank()) {
            Span(attrs = { style { fontSize(1.8.em); fontWeight("800"); color(valueColor); lineHeight("1") } }) {
                Text(value)
            }
        }
        Span(attrs = { style { fontSize(0.82.em); fontWeight("600"); color(KaihuTheme.TextSecondary) } }) {
            Text(label)
        }
    }
}

// ======================
// Ylänappi (TopBar)
// ======================

